import React from 'react';
import { Link } from 'react-router-dom';
import { MessageSquare, ArrowLeft } from 'lucide-react';
import '../styles/Terms.css';

const Terms = () => {
  return (
    <div className="terms-container">
      {/* Header */}
      <header className="terms-header">
        <div className="container">
          <Link to="/" className="back-link">
            <ArrowLeft size={20} />
            Back to Home
          </Link>
          <div className="terms-brand">
            <MessageSquare size={24} />
            <span>Sheets2WhatsApp</span>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="terms-content">
        <div className="container">
          <div className="terms-nav">
            <a href="#terms" className="terms-nav-link">Terms of Service</a>
            <a href="#privacy" className="terms-nav-link">Privacy Policy</a>
            <a href="#refund" className="terms-nav-link">Refund Policy</a>
          </div>

          {/* Terms of Service */}
          <section id="terms" className="terms-section">
            <h1>Terms of Service</h1>
            <p className="terms-updated">Last updated: February 28, 2026</p>

            <h2>1. Acceptance of Terms</h2>
            <p>
              By accessing and using Sheets2WhatsApp ("the Service"), you accept and agree to be bound by the terms 
              and provisions of this agreement. If you do not agree to abide by the above, please do not use this service.
            </p>

            <h2>2. Description of Service</h2>
            <p>
              Sheets2WhatsApp is a Google Workspace add-on that enables users to send WhatsApp messages directly from 
              Google Sheets using the official WhatsApp Cloud API. The Service provides:
            </p>
            <ul>
              <li>Integration with WhatsApp Cloud API for message delivery</li>
              <li>Support for Meta-approved and custom message templates</li>
              <li>Bulk messaging capabilities with intelligent rate limiting</li>
              <li>Real-time status tracking and delivery updates</li>
              <li>Phone number validation and formatting</li>
            </ul>

            <h2>3. User Responsibilities</h2>
            <p>As a user of Sheets2WhatsApp, you agree to:</p>
            <ul>
              <li>Provide accurate WhatsApp Business API credentials</li>
              <li>Comply with WhatsApp's Business and Commerce Policies</li>
              <li>Obtain proper consent from recipients before sending messages</li>
              <li>Not use the Service for spam, harassment, or illegal activities</li>
              <li>Maintain the security of your account credentials</li>
              <li>Be responsible for all activity that occurs under your account</li>
            </ul>

            <h2>4. WhatsApp API Compliance</h2>
            <p>
              Users must maintain an active WhatsApp Business API account and comply with all Meta/WhatsApp policies. 
              Sheets2WhatsApp is not responsible for:
            </p>
            <ul>
              <li>Message delivery failures due to WhatsApp API restrictions</li>
              <li>Account suspensions or bans imposed by Meta/WhatsApp</li>
              <li>Rate limits or throttling applied by Meta/WhatsApp</li>
              <li>Changes to WhatsApp API policies or pricing</li>
            </ul>

            <h2>5. Pricing and Billing</h2>
            <p>
              Sheets2WhatsApp offers multiple subscription tiers. By subscribing to a paid plan, you agree to:
            </p>
            <ul>
              <li>Pay all applicable fees for your selected plan</li>
              <li>Automatic renewal of your subscription unless cancelled</li>
              <li>Billing cycles as specified in your plan (monthly or yearly)</li>
              <li>Message limits as specified in your plan tier</li>
            </ul>
            <p>
              Message limits apply per calendar month. Unused messages do not roll over to the next month. 
              The Ultimate plan offers unlimited messages with a bulk sending guardrail of 40,000 messages per day.
            </p>

            <h2>6. Service Availability</h2>
            <p>
              While we strive to provide reliable service, Sheets2WhatsApp does not guarantee uninterrupted access. 
              The Service may be temporarily unavailable due to:
            </p>
            <ul>
              <li>Scheduled maintenance</li>
              <li>Emergency maintenance or security updates</li>
              <li>Third-party service disruptions (Google, WhatsApp, hosting providers)</li>
              <li>Force majeure events</li>
            </ul>

            <h2>7. Data Processing and Storage</h2>
            <p>
              Sheets2WhatsApp processes data only during active messaging sessions. We do not:
            </p>
            <ul>
              <li>Store recipient contact information on our servers</li>
              <li>Retain message content after delivery</li>
              <li>Share your data with third parties for marketing purposes</li>
            </ul>
            <p>
              Your WhatsApp API credentials are securely stored using Google's infrastructure. 
              See our Privacy Policy for detailed information.
            </p>

            <h2>8. Intellectual Property</h2>
            <p>
              The Service, including its original content, features, and functionality, is owned by Sheets2WhatsApp 
              and is protected by international copyright, trademark, and other intellectual property laws.
            </p>

            <h2>9. Limitation of Liability</h2>
            <p>
              Sheets2WhatsApp shall not be liable for any indirect, incidental, special, consequential, or punitive 
              damages resulting from:
            </p>
            <ul>
              <li>Your use or inability to use the Service</li>
              <li>Unauthorized access to your data</li>
              <li>Errors or omissions in the Service</li>
              <li>WhatsApp API failures or restrictions</li>
              <li>Message delivery failures</li>
            </ul>

            <h2>10. Termination</h2>
            <p>
              We reserve the right to terminate or suspend your account immediately, without prior notice, for:
            </p>
            <ul>
              <li>Violation of these Terms of Service</li>
              <li>Fraudulent or illegal activity</li>
              <li>Abuse of the Service or other users</li>
              <li>Non-payment of fees</li>
            </ul>
            <p>
              You may terminate your account at any time through your account settings or by contacting support.
            </p>

            <h2>11. Changes to Terms</h2>
            <p>
              We reserve the right to modify these terms at any time. We will notify users of significant changes 
              via email or through the Service. Continued use of the Service after changes constitutes acceptance 
              of the modified terms.
            </p>

            <h2>12. Governing Law</h2>
            <p>
              These Terms shall be governed by and construed in accordance with the laws of India, without regard 
              to its conflict of law provisions.
            </p>

            <h2>13. Contact Information</h2>
            <p>
              For questions about these Terms of Service, please contact us at:
              <br />
              <a href="mailto:contact@projectdns.in">contact@projectdns.in</a>
            </p>
          </section>

          {/* Privacy Policy */}
          <section id="privacy" className="terms-section">
            <h1>Privacy Policy</h1>
            <p className="terms-updated">Last updated: February 28, 2026</p>

            <h2>1. Information We Collect</h2>
            <p>When you use Sheets2WhatsApp, we collect:</p>
            <ul>
              <li><strong>Account Information:</strong> Email address, name, and Google account details</li>
              <li><strong>API Credentials:</strong> WhatsApp Business API tokens and configuration</li>
              <li><strong>Usage Data:</strong> Message counts, send times, and delivery status</li>
              <li><strong>Technical Data:</strong> Browser type, IP address, and access times</li>
            </ul>

            <h2>2. How We Use Your Information</h2>
            <p>We use collected information to:</p>
            <ul>
              <li>Provide and maintain the Service</li>
              <li>Process WhatsApp message delivery</li>
              <li>Monitor usage for billing purposes</li>
              <li>Improve Service performance and features</li>
              <li>Communicate with you about the Service</li>
              <li>Detect and prevent fraud or abuse</li>
            </ul>

            <h2>3. Data Storage and Security</h2>
            <p>
              We implement industry-standard security measures to protect your data:
            </p>
            <ul>
              <li>Encryption of API credentials at rest and in transit</li>
              <li>Secure storage using Google Cloud infrastructure</li>
              <li>Regular security audits and updates</li>
              <li>Access controls and authentication requirements</li>
            </ul>
            <p>
              <strong>Important:</strong> We do not permanently store recipient contact information or message content. 
              Data is processed in real-time and discarded after delivery.
            </p>

            <h2>4. Data Sharing</h2>
            <p>We do not sell your personal information. We may share data with:</p>
            <ul>
              <li><strong>Service Providers:</strong> Google (for hosting), Meta (for WhatsApp API)</li>
              <li><strong>Legal Requirements:</strong> When required by law or to protect our rights</li>
              <li><strong>Business Transfers:</strong> In connection with mergers or acquisitions</li>
            </ul>

            <h2>5. Your Rights</h2>
            <p>You have the right to:</p>
            <ul>
              <li>Access your personal information</li>
              <li>Request correction of inaccurate data</li>
              <li>Request deletion of your account and data</li>
              <li>Export your usage data</li>
              <li>Opt out of marketing communications</li>
            </ul>

            <h2>6. Google OAuth Scopes</h2>
            <p>
              Sheets2WhatsApp requests limited access to your Google account to:
            </p>
            <ul>
              <li>Read and write data in Google Sheets (to process messages and update status)</li>
              <li>Display add-on sidebar interface</li>
            </ul>
            <p>
              We only access data in sheets where you explicitly use the add-on. We do not access other 
              files in your Google Drive.
            </p>

            <h2>7. Cookies and Tracking</h2>
            <p>
              We use essential cookies to maintain your session and preferences. We do not use tracking cookies 
              for advertising purposes.
            </p>

            <h2>8. Children's Privacy</h2>
            <p>
              Our Service is not intended for users under 18 years of age. We do not knowingly collect information 
              from children.
            </p>

            <h2>9. International Data Transfers</h2>
            <p>
              Your data may be transferred to and processed in countries outside your jurisdiction. We ensure 
              appropriate safeguards are in place for such transfers.
            </p>

            <h2>10. Changes to Privacy Policy</h2>
            <p>
              We may update this Privacy Policy periodically. We will notify you of significant changes via email 
              or through the Service.
            </p>

            <h2>11. Contact Us</h2>
            <p>
              For privacy-related questions or requests, contact us at:
              <br />
              <a href="mailto:contact@projectdns.in">contact@projectdns.in</a>
            </p>
          </section>

          {/* Refund Policy */}
          <section id="refund" className="terms-section">
            <h1>Refund Policy</h1>
            <p className="terms-updated">Last updated: February 28, 2026</p>

            <h2>1. General Policy</h2>
            <p>
              We want you to be completely satisfied with Sheets2WhatsApp. If you're not happy with your purchase, 
              we offer refunds under the following conditions.
            </p>

            <h2>2. Eligibility for Refunds</h2>
            <p>You may be eligible for a refund if:</p>
            <ul>
              <li>You request a refund within 7 days of your initial purchase</li>
              <li>You have not exceeded 50% of your monthly message quota</li>
              <li>The Service has a critical technical issue preventing its use</li>
              <li>You were charged incorrectly due to a billing error</li>
            </ul>

            <h2>3. Non-Refundable Situations</h2>
            <p>Refunds will not be provided for:</p>
            <ul>
              <li>Message delivery failures caused by WhatsApp API restrictions</li>
              <li>Account suspensions due to violation of WhatsApp policies</li>
              <li>User error or misconfiguration</li>
              <li>Changes in WhatsApp API pricing or policies</li>
              <li>Requests made after 7 days from purchase</li>
              <li>Services already consumed (messages sent)</li>
            </ul>

            <h2>4. Free Plan</h2>
            <p>
              The Free plan includes 50 one-time messages. As this is a free offering, no refunds apply.
            </p>

            <h2>5. Subscription Cancellations</h2>
            <p>
              You may cancel your subscription at any time. Cancellations take effect at the end of your current 
              billing period. No prorated refunds are provided for partial months.
            </p>
            <ul>
              <li>Access continues until the end of the paid period</li>
              <li>Unused message quotas are forfeited upon cancellation</li>
              <li>No refund for the current billing cycle unless within the 7-day window</li>
            </ul>

            <h2>6. Yearly Subscriptions</h2>
            <p>
              Yearly subscriptions offer a discount but have stricter refund terms:
            </p>
            <ul>
              <li>Full refund available within 7 days of purchase</li>
              <li>After 7 days, no refunds for remaining months</li>
              <li>Service access continues until the end of the paid year</li>
            </ul>

            <h2>7. How to Request a Refund</h2>
            <p>
              To request a refund:
            </p>
            <ol>
              <li>Contact us at <a href="mailto:contact@projectdns.in">contact@projectdns.in</a></li>
              <li>Include your account email and order number</li>
              <li>Explain the reason for your refund request</li>
              <li>Allow 5-7 business days for processing</li>
            </ol>

            <h2>8. Refund Processing</h2>
            <p>
              Approved refunds will be processed to the original payment method within 7-10 business days. 
              You will receive an email confirmation once the refund is processed.
            </p>

            <h2>9. Disputed Charges</h2>
            <p>
              If you believe you were charged incorrectly, please contact us before filing a chargeback. 
              We will investigate and resolve billing issues promptly.
            </p>

            <h2>10. Plan Upgrades and Downgrades</h2>
            <ul>
              <li><strong>Upgrades:</strong> Take effect immediately with prorated credit</li>
              <li><strong>Downgrades:</strong> Take effect at the start of next billing cycle</li>
            </ul>

            <h2>11. Service Credits</h2>
            <p>
              In cases where a refund is not appropriate but you experienced service issues, we may offer 
              service credits at our discretion.
            </p>

            <h2>12. Changes to Refund Policy</h2>
            <p>
              We reserve the right to modify this Refund Policy at any time. Changes will be communicated 
              via email and take effect for purchases made after the update date.
            </p>

            <h2>13. Contact Information</h2>
            <p>
              For refund requests or questions, contact us at:
              <br />
              <a href="mailto:contact@projectdns.in">contact@projectdns.in</a>
            </p>
          </section>
        </div>
      </main>

      {/* Footer */}
      <footer className="terms-footer">
        <div className="container">
          <p>&copy; 2026 Sheets2WhatsApp. All rights reserved.</p>
          <Link to="/">Return to Home</Link>
        </div>
      </footer>
    </div>
  );
};

export default Terms;
